import { useState, useEffect } from 'react';
import { X, Calendar, Package, Tag, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { ReasonCodeSelect, getEffectiveReason, useReasonCodeIds, type ProposedChangeLeg, type WorkflowModalProps } from './AcknowledgmentSharedUtils';
import { CUSTOMER_REQUIRED_DATE_SHORT, SUPPLIER_PROMISE_DATE_SHORT } from '../../config/dateLabels';
import { formatUnitPrice } from '../../lib/unitPrice';
import { LineIdentityPanel } from './LineIdentityPanel';

// Extended interface for Propose Change modal (supports multiple change types)
interface ProposeChangeModalProps extends WorkflowModalProps {
  currentQuantity: string;
  currentUnitPrice: string;
  userType?: 'customer' | 'supplier';
  /** CR-7739: the line's own site's Hide Propose Qty Ordered (from the line
   * payload). Undefined falls back to the session's value. */
  quantityProposalsHidden?: boolean;
}

// CR-3401: Comprehensive Propose Change Modal - supports multiple simultaneous changes
export function ProposeChangeModal({
  isOpen,
  onClose,
  lineId,
  lineNumber,
  partNumber,
  orderNumber,
  currentDeliveryDate,
  currentQuantity,
  currentUnitPrice,
  onSubmit,
  userType,
  quantityProposalsHidden,
}: ProposeChangeModalProps) {
  const { formatDate, formatQuantity, preferences } = useFormatters();
  const { isOpenOrdersControlHidden } = useMenuSettings();
  // CR-5884 / CR-7739: "Hide Propose Qty Ordered" hides Quantity from a supplier,
  // decided by the line's own customer site (CR-4722 hides it from customers).
  const hideProposeQtyForSupplier = userType === 'supplier' && (quantityProposalsHidden
    ?? isOpenOrdersControlHidden('hide_propose_qty_ordered', true));
  // CR-3401: Changed to checkboxes for multiple selections
  const [changeDate, setChangeDate] = useState(false);
  const [changeQuantity, setChangeQuantity] = useState(false);
  const [changePrice, setChangePrice] = useState(false);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedQuantity, setProposedQuantity] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  // Per-field reason codes. CR-8589: plus the code id picked, so it counts as used.
  const [dateReasonSelect, setDateReasonSelect] = useState('');
  const [dateCustomReason, setDateCustomReason] = useState('');
  const [qtyReasonSelect, setQtyReasonSelect] = useState('');
  const [qtyCustomReason, setQtyCustomReason] = useState('');
  const [priceReasonSelect, setPriceReasonSelect] = useState('');
  const [priceCustomReason, setPriceCustomReason] = useState('');
  const { withReasonCodeIds, reasonChange, resetReasonCodeIds } = useReasonCodeIds();
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasAnyChange = changeDate || changeQuantity || changePrice;

  /**
   * CR-7066: parse a numeric input the way the backend does.
   *
   * The old code sent `parseFloat(value) || 0`, which turned an empty or
   * unparseable box into a zero the server then had to reject — and, on the
   * spreadsheet path, a zero that was written straight onto the line.
   * Returns null when the box does not hold a usable number, so the caller
   * reports it rather than substituting a value the user never typed.
   */
  const parseNumericInput = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // CR-4866: Seed proposed date with the existing promise date when modal opens,
  // so the date picker defaults to the current value rather than today.
  useEffect(() => {
    if (!isOpen || !currentDeliveryDate) return;
    const d = new Date(currentDeliveryDate);
    if (Number.isNaN(d.getTime())) return;
    setProposedDate(d.toISOString().split('T')[0]);
  }, [isOpen, currentDeliveryDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - at least one change type must be selected
    if (!hasAnyChange) {
      toast.error('Please select at least one type of change');
      return;
    }

    // CR-3401: Validate each selected change type
    if (changeDate && !proposedDate) {
      toast.error('Please select a proposed delivery date');
      return;
    }
    // CR-7066: validate the numbers themselves, not just that a box was
    // filled in. Zero, negative and no-op values are rejected here with a
    // clear message; the backend enforces the same rules regardless.
    const quantityValue = parseNumericInput(proposedQuantity);
    if (changeQuantity) {
      if (quantityValue === null) {
        toast.error('Please enter a proposed quantity');
        return;
      }
      if (quantityValue <= 0) {
        toast.error('Proposed quantity must be greater than zero');
        return;
      }
      if (quantityValue === parseFloat(currentQuantity)) {
        toast.error('Proposed quantity must be different from the current quantity');
        return;
      }
    }

    const priceValue = parseNumericInput(proposedPrice);
    if (changePrice) {
      if (priceValue === null) {
        toast.error('Please enter a proposed price');
        return;
      }
      if (priceValue <= 0) {
        toast.error('Proposed price must be greater than zero');
        return;
      }
      if (priceValue === parseFloat(currentUnitPrice)) {
        toast.error('Proposed price must be different from the current price');
        return;
      }
    }

    // Validate each selected field has a reason code
    // CR-7166: Use role-appropriate label in validation message
    if (changeDate && !getEffectiveReason(dateReasonSelect, dateCustomReason)) {
      const dateLabel = userType === 'customer' ? CUSTOMER_REQUIRED_DATE_SHORT : SUPPLIER_PROMISE_DATE_SHORT;
      toast.error(`Please select a reason code for the ${dateLabel} change`);
      return;
    }
    if (changeQuantity && !getEffectiveReason(qtyReasonSelect, qtyCustomReason)) {
      toast.error('Please select a reason code for the Quantity change');
      return;
    }
    if (changePrice && !getEffectiveReason(priceReasonSelect, priceCustomReason)) {
      toast.error('Please select a reason code for the Unit Price change');
      return;
    }

    setSubmitting(true);
    try {
      // CR-3401: all the changes together, with per-field reason codes.
      // EXPEDITING-PARITY PR 2 (G12): ONE request, not one per field. This loop
      // used to `await onSubmit(...)` per leg, so a price the validator refused
      // left the date already committed and the other side saw a change request
      // that did not match what was typed. The server now runs the legs in one
      // transaction; each still becomes its own change request, because accept
      // and reject are per field.
      const changes: ProposedChangeLeg[] = [];
      if (changeDate) {
        changes.push({
          field: 'date',
          value: proposedDate,
          reason: getEffectiveReason(dateReasonSelect, dateCustomReason),
        });
      }
      // The null checks cannot fail — validation above returns early — but
      // they let TypeScript narrow without an assertion.
      if (changeQuantity && quantityValue !== null) {
        changes.push({
          field: 'quantity',
          value: String(quantityValue),
          reason: getEffectiveReason(qtyReasonSelect, qtyCustomReason),
        });
      }
      if (changePrice && priceValue !== null) {
        changes.push({
          field: 'price',
          value: String(priceValue),
          reason: getEffectiveReason(priceReasonSelect, priceCustomReason),
        });
      }

      await onSubmit({ lineId, action: 'proposeChanges', changes: withReasonCodeIds(changes), comments });

      // Reset form
      setProposedDate('');
      setProposedQuantity('');
      setProposedPrice('');
      setDateReasonSelect('');
      setDateCustomReason('');
      setQtyReasonSelect('');
      setQtyCustomReason('');
      setPriceReasonSelect('');
      setPriceCustomReason('');
      resetReasonCodeIds();
      setComments('');
      setChangeDate(false);
      setChangeQuantity(false);
      setChangePrice(false);
      onClose();

      if (changes.length > 1) {
        toast.success(`Successfully proposed ${changes.length} changes`);
      }
    } catch (error) {
      console.error('Failed to propose change:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Propose Change</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <LineIdentityPanel
              lineNumber={lineNumber}
              partNumber={partNumber}
              orderNumber={orderNumber}
              className="bg-orange-50 border border-orange-200"
            />

            {/* CR-3401: Change Type Selection with Checkboxes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                What would you like to change? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setChangeDate(!changeDate)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changeDate
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changeDate}
                      onChange={() => setChangeDate(!changeDate)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Calendar className="h-5 w-5" />
                  </div>
                  {/* CR-7166: Show role-appropriate label */}
                  <span className="text-xs font-medium">
                    {userType === 'customer' ? CUSTOMER_REQUIRED_DATE_SHORT : SUPPLIER_PROMISE_DATE_SHORT}
                  </span>
                </button>
                {/* CR-4722: Quantity and Price buttons hidden for customer users.
                    CR-5884: Quantity also hidden for suppliers when admin
                    has enabled "Hide Propose Qty Ordered". */}
                {userType !== 'customer' && !hideProposeQtyForSupplier && (
                <button
                  type="button"
                  onClick={() => setChangeQuantity(!changeQuantity)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changeQuantity
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changeQuantity}
                      onChange={() => setChangeQuantity(!changeQuantity)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Package className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium">Quantity</span>
                </button>
                )}
                {userType !== 'customer' && (
                <button
                  type="button"
                  onClick={() => setChangePrice(!changePrice)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changePrice
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changePrice}
                      onChange={() => setChangePrice(!changePrice)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {/* Batch 4 (Aaron, 17 Sep 2026): a generic price mark,
                        not a currency one — this line can be in any currency
                        and a $ is a claim about which. */}
                    <Tag className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium">Unit Price</span>
                </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Select one or more change types</p>
            </div>

            {/* CR-3401: Delivery Date Change Section */}
            {/* CR-7166: Show role-appropriate section header */}
            {changeDate && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">
                    {userType === 'customer' ? `${CUSTOMER_REQUIRED_DATE_SHORT} Change` : `${SUPPLIER_PROMISE_DATE_SHORT} Change`}
                  </span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    <span>{formatDate(currentDeliveryDate)}</span>
                  </div>
                </div>
                <input
                  type="date"
                  value={proposedDate}
                  onChange={(e) => setProposedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                />
                {/* CR-7166: Show role-appropriate reason code label */}
                <ReasonCodeSelect
                  value={dateReasonSelect}
                  onChange={reasonChange('date', setDateReasonSelect)}
                  customValue={dateCustomReason}
                  onCustomChange={setDateCustomReason}
                  required
                  label={userType === 'customer' ? `${CUSTOMER_REQUIRED_DATE_SHORT} Reason Code` : `${SUPPLIER_PROMISE_DATE_SHORT} Reason Code`}
                  categories={['date_change']}
                />
              </div>
            )}

            {/* CR-3401: Quantity Change Section */}
            {changeQuantity && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Quantity Change</span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    <span>{formatQuantity(currentQuantity)}</span>
                  </div>
                </div>
                <input
                  type="number"
                  value={proposedQuantity}
                  onChange={(e) => setProposedQuantity(e.target.value)}
                  // CR-7066: no `min`, for the same reason as the price input
                  // below — it would pre-empt handleSubmit and replace our
                  // message with the browser's.
                  step="1"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  placeholder="Enter proposed quantity"
                />
                <ReasonCodeSelect
                  value={qtyReasonSelect}
                  onChange={reasonChange('quantity', setQtyReasonSelect)}
                  customValue={qtyCustomReason}
                  onCustomChange={setQtyCustomReason}
                  required
                  label="Quantity Reason Code"
                  categories={['quantity']}
                />
              </div>
            )}

            {/* CR-3401: Price Change Section */}
            {changePrice && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                {/* Walkthrough batch 3 (Aaron, 17 Sep 2026, relaying Sarah):
                    no currency glyph on the heading. This line can be in any
                    currency, and a hard-coded $ beside the words is a claim
                    about which one. */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-orange-700">Unit Price Change</span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    {/* A plain number at the tenant's price decimals. It used
                        to take neither the line's currency nor its decimals, so
                        a 117.860 USD line read "$118" in the default currency,
                        rounded — and batch 4 removed the symbol altogether
                        (lib/amountDisplay). */}
                    <span>{formatUnitPrice(currentUnitPrice, preferences?.unit_price_decimals ?? 2)}</span>
                  </div>
                </div>
                <input
                  type="number"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  // CR-7066: deliberately no `min`. A native min blocks the
                  // form's submit event, so handleSubmit never runs and the
                  // user gets the browser's "Value must be greater than or
                  // equal to 0.01" tooltip instead of our own message. The
                  // rule is enforced in handleSubmit and again server-side.
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  /* No placeholder: it named GBP on lines priced in anything
                     else, and the row above already says what the current price
                     is. The box still needs an accessible NAME, though — without
                     the placeholder it had none at all, which is a real defect
                     for anyone using a screen reader and not merely a broken
                     test selector. */
                  aria-label="Proposed unit price"
                />
                <ReasonCodeSelect
                  value={priceReasonSelect}
                  onChange={reasonChange('price', setPriceReasonSelect)}
                  customValue={priceCustomReason}
                  onCustomChange={setPriceCustomReason}
                  required
                  label="Unit Price Reason Code"
                  categories={['price']}
                />
              </div>
            )}

            {/* Optional additional comments */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide additional details about the proposed change..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !hasAnyChange}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Proposing...
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4" />
                    {/* CR-3401: Show count of changes being proposed */}
                    Propose {[changeDate, changeQuantity, changePrice].filter(Boolean).length} Change{[changeDate, changeQuantity, changePrice].filter(Boolean).length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
